import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {testIdProps} from '@github-ui/test-id-props'
import {useIgnoreKeyboardActionsWhileComposing} from '@github-ui/use-ignore-keyboard-actions-while-composing'
import {useSyncedState} from '@github-ui/use-synced-state'
import {DownloadIcon, GraphIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, FormControl, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {memo, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react'

import {ViewOptionsMenuUI} from '../../api/stats/contracts'
import {ViewTypeParam} from '../../api/view/contracts'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {useDownloadView} from '../../hooks/use-download-view'
import {useIsCommandPaletteVisible} from '../../hooks/use-is-command-palette-visible'
import {useMemexProjectViewRootHeight} from '../../hooks/use-memex-app-root-height'
import type {BaseViewState, ViewIsDirtyStates} from '../../hooks/use-view-state-reducer/types'
import {useViews} from '../../hooks/use-views'
import {Link} from '../../router'
import {useProjectRouteParams} from '../../router/use-project-route-params'
import {PROJECT_INSIGHTS_ROUTE} from '../../routes'
import {useIssueFieldsEnabled} from '../../state-providers/issue-fields/use-issue-fields-enabled'
import {useAddFieldModal} from '../../state-providers/modals/use-add-field-modal'
import {useUserNotices} from '../../state-providers/user-notices/user-notices-provider'
import {useViewOptionsMenuRef} from '../../state-providers/view-options/use-view-options-menu-ref'
import {Resources} from '../../strings'
import {EmojiAutocomplete} from '../common/emoji-autocomplete'
import {useSearch} from '../filter-bar/search-context'
import {ConfigurationItemMenus, ConfigurationItems} from './configuration-items'
import {HierarchyToggle} from './hierarchy-toggle'
import {LayoutToggle} from './layout-toggle'
import styles from './menu.module.css'
import {MenuAnchor} from './menu-anchor'
import {RoadmapUserSettingsMenu} from './roadmap-user-settings'
import {ViewActionItems} from './view-action-items'
import {ViewChangeButtons} from './view-changes-button'

type ViewOptionsMenuProps = {
  view: BaseViewState & ViewIsDirtyStates
  viewOptionMenuTriggerRef?: React.RefObject<HTMLButtonElement | null>
  showViewOptionsMenu?: () => void
}

export const ViewOptionsMenu = memo(function ViewOptionsMenu({
  view,
  viewOptionMenuTriggerRef,
  showViewOptionsMenu,
}: ViewOptionsMenuProps) {
  const memex_nested_sub_issues = useFeatureFlag('memex_nested_sub_issues')
  const {hideUserNotice} = useUserNotices()
  const displayButtonEnabled = useFeatureFlag('memex_display_button_config_menu')

  const [open, setOpen] = useState(false)
  const [viewMenuOpen, setViewMenuOpen] = useState(false)
  const [showSubMenu, setShowSubMenu] = useState<
    | null
    | 'Fields'
    | 'Sort by'
    | 'Group by'
    | 'Column by'
    | 'Field sum'
    | 'Dates'
    | 'Zoom level'
    | 'rename-view'
    | 'Marker fields'
    | 'Slice by'
    | 'Swimlanes'
  >(null)
  const {clientHeight} = useMemexProjectViewRootHeight({
    onResize: () => {
      if (open) {
        // Use queueMicrotask to avoid flushSync warning when called during React rendering
        queueMicrotask(() => {
          setOpen(false)
          // Re-open after state update to trigger re-positioning
          requestAnimationFrame(() => {
            setOpen(true)
          })
        })
      }
    },
  })
  const {query} = useSearch()

  const {views, currentView, duplicateCurrentViewState, destroyCurrentView} = useViews()

  const {hasWritePermissions} = ViewerPrivileges()
  const {anchorRef, registerMenuControls, setIsMenuOpen} = useViewOptionsMenuRef()
  const viewMenuAnchorRef = useRef<HTMLButtonElement | null>(null)
  const displayMenuScrollRef = useRef<HTMLDivElement | null>(null)
  const viewMenuScrollRef = useRef<HTMLDivElement | null>(null)
  const effectiveMenuAnchorRef = displayButtonEnabled ? viewMenuAnchorRef : anchorRef
  const configRef = useRef<HTMLLIElement | null>(null)
  const previousClientHeightRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    // Skip updates to avoid unnecessary DOM manipulation
    if (previousClientHeightRef.current === clientHeight) {
      return
    }
    previousClientHeightRef.current = clientHeight

    const maxHeightValue = `${clientHeight}px`
    if (displayMenuScrollRef.current) {
      displayMenuScrollRef.current.style.setProperty('--max-height', maxHeightValue)
    }
    if (viewMenuScrollRef.current) {
      viewMenuScrollRef.current.style.setProperty('--max-height', maxHeightValue)
    }
  }, [clientHeight])

  useEffect(() => {
    if (!open) return
    setShowSubMenu(null)
  }, [open])

  useEffect(() => {
    setIsMenuOpen(open)
  }, [open, setIsMenuOpen])

  const hideAnchoredUserNotice = useCallback(() => {
    hideUserNotice('memex_issue_types_rename_prompt')
  }, [hideUserNotice])

  const closeMenu = useCallback(() => {
    setOpen(false)
  }, [])

  const openMenu = useCallback(() => {
    hideAnchoredUserNotice()
    setOpen(true)
    if (displayButtonEnabled) {
      setViewMenuOpen(false)
    }
  }, [displayButtonEnabled, hideAnchoredUserNotice])

  const toggleMenu = useCallback(() => {
    hideAnchoredUserNotice()
    setOpen(state => {
      const nextState = !state
      if (displayButtonEnabled && nextState) {
        setViewMenuOpen(false)
      }
      return nextState
    })
  }, [displayButtonEnabled, hideAnchoredUserNotice])

  const closeViewMenu = useCallback(() => {
    if (displayButtonEnabled) {
      setViewMenuOpen(false)
    } else {
      closeMenu()
    }
  }, [closeMenu, displayButtonEnabled])

  const openViewMenu = useCallback(() => {
    if (displayButtonEnabled) {
      hideAnchoredUserNotice()
      setViewMenuOpen(true)
      setOpen(false)
    } else {
      openMenu()
    }
  }, [displayButtonEnabled, hideAnchoredUserNotice, openMenu])

  const toggleViewMenu = useCallback(() => {
    if (displayButtonEnabled) {
      hideAnchoredUserNotice()
      setViewMenuOpen(state => {
        const nextState = !state
        if (nextState) {
          setOpen(false)
        }
        return nextState
      })
    } else {
      toggleMenu()
    }
  }, [displayButtonEnabled, hideAnchoredUserNotice, toggleMenu])

  useEffect(() => {
    registerMenuControls({
      toggle: toggleMenu,
      open: openMenu,
      close: closeMenu,
    })

    return () => {
      registerMenuControls(null)
    }
  }, [closeMenu, openMenu, registerMenuControls, toggleMenu])

  const handleDestroyView = useCallback(async () => {
    destroyCurrentView(
      view.number,
      {
        ui: ViewOptionsMenuUI,
      },
      () => {
        closeViewMenu()
        effectiveMenuAnchorRef.current?.focus()
      },
    )
  }, [closeViewMenu, destroyCurrentView, effectiveMenuAnchorRef, view.number])

  const handleDuplicateView = useCallback(async () => {
    if (!currentView) return
    await duplicateCurrentViewState(currentView.number, undefined, {
      ui: ViewOptionsMenuUI,
    })
    closeViewMenu()
  }, [closeViewMenu, currentView, duplicateCurrentViewState])

  const handleRenameViewClick = useCallback(() => {
    closeViewMenu()
    setShowSubMenu('rename-view')
  }, [closeViewMenu])

  useImperativeHandle<HTMLButtonElement | null, HTMLButtonElement | null>(
    viewOptionMenuTriggerRef,
    () => effectiveMenuAnchorRef.current,
  )

  const {isCommandPaletteVisible} = useIsCommandPaletteVisible()

  const downloadView = useDownloadView()
  const handleDownloadView = useCallback(async () => {
    await downloadView()
    effectiveMenuAnchorRef.current?.focus()
  }, [downloadView, effectiveMenuAnchorRef])
  const projectRouteParams = useProjectRouteParams()

  const issueFieldsEnabled = useIssueFieldsEnabled()
  const {showAddFieldModal} = useAddFieldModal()
  const preventSubmenuClose = issueFieldsEnabled && showAddFieldModal

  const handleOpenChange = useCallback(
    (nextOpen: boolean): void => {
      // Prevent the parent ActionMenu from closing while a modal-like sub-menu/dialog is open
      if (!preventSubmenuClose) {
        if (nextOpen) {
          openMenu()
        } else {
          closeMenu()
        }
      }
    },
    [closeMenu, openMenu, preventSubmenuClose],
  )
  const handleViewMenuOpenChange = useCallback(
    (nextOpen: boolean): void => {
      if (!displayButtonEnabled) {
        handleOpenChange(nextOpen)
        return
      }

      if (nextOpen) {
        openViewMenu()
      } else {
        closeViewMenu()
      }
    },
    [closeViewMenu, displayButtonEnabled, handleOpenChange, openViewMenu],
  )

  const menuVariantForViewAnchor = displayButtonEnabled ? 'view' : 'combined'

  const renderOverlayContent = (
    variant: 'display' | 'view' | 'combined',
    containerRef: React.RefObject<HTMLDivElement | null>,
  ): React.ReactNode => {
    const includeDisplayOptions = variant === 'display' || variant === 'combined'
    const includeViewOptions = variant === 'view' || variant === 'combined'

    return (
      <div ref={containerRef} className={styles.scrollContainer}>
        {includeDisplayOptions ? (
          <>
            <LayoutToggle view={view} />
            <ActionList.Divider />
          </>
        ) : null}
        <ActionList className={clsx(!displayButtonEnabled && 'pt-0', includeDisplayOptions && 'pt-0')}>
          {includeDisplayOptions ? (
            <>
              <ActionList.Divider />
              <ConfigurationItems
                selectedOption={showSubMenu === 'rename-view' ? null : showSubMenu}
                onSelect={option => {
                  setShowSubMenu(option)
                }}
                ref={configRef}
              />
              <ConfigurationItemMenus
                anchorRef={configRef}
                currentMenu={showSubMenu === 'rename-view' ? null : showSubMenu}
                setShowSubMenu={setShowSubMenu}
              />
              {view.localViewState.layout === ViewTypeParam.Roadmap ? (
                <>
                  <ActionList.Divider />
                  <ActionList.Group selectionVariant="multiple" aria-label="User settings">
                    <RoadmapUserSettingsMenu />
                  </ActionList.Group>
                </>
              ) : null}
            </>
          ) : null}

          {memex_nested_sub_issues && includeDisplayOptions && view.localViewState.layout === ViewTypeParam.Table ? (
            <>
              <ActionList.Divider />
              <ActionList.Item
                className={styles.hierarchyToggleContainer}
                onSelect={e => {
                  // Prevent menu from closing when toggling the switch
                  e.preventDefault()
                }}
              >
                <HierarchyToggle view={view} />
              </ActionList.Item>
            </>
          ) : null}

          {includeViewOptions ? (
            <>
              <ActionList.Divider />
              {hasWritePermissions ? (
                <>
                  {!displayButtonEnabled ? (
                    <>
                      <ActionList.Group>
                        <ActionList.LinkItem
                          as={Link}
                          {...testIdProps('view-options-menu-item-show-insights')}
                          to={{
                            pathname: PROJECT_INSIGHTS_ROUTE.generatePath(projectRouteParams),
                            search: query
                              ? `${new URLSearchParams({
                                  filterQuery: query,
                                })}`
                              : undefined,
                          }}
                        >
                          <ActionList.LeadingVisual>
                            <GraphIcon />
                          </ActionList.LeadingVisual>
                          Generate chart
                        </ActionList.LinkItem>
                      </ActionList.Group>
                      <ActionList.Divider />
                    </>
                  ) : null}
                  <ViewActionItems
                    returnFocusRef={effectiveMenuAnchorRef}
                    handleRenameViewClick={handleRenameViewClick}
                    handleDuplicateView={handleDuplicateView}
                    handleDestroyView={handleDestroyView}
                    viewsLength={views.length}
                    view={view}
                  />
                  <ActionList.Divider />
                </>
              ) : null}

              {displayButtonEnabled ? (
                <>
                  <ActionList.Group>
                    <ActionList.LinkItem
                      as={Link}
                      {...testIdProps('view-options-menu-item-show-insights')}
                      to={{
                        pathname: PROJECT_INSIGHTS_ROUTE.generatePath(projectRouteParams),
                        search: query
                          ? `${new URLSearchParams({
                              filterQuery: query,
                            })}`
                          : undefined,
                      }}
                    >
                      <ActionList.LeadingVisual>
                        <GraphIcon />
                      </ActionList.LeadingVisual>
                      Generate chart
                    </ActionList.LinkItem>
                  </ActionList.Group>
                </>
              ) : null}
              <ActionList.Item onSelect={handleDownloadView}>
                <ActionList.LeadingVisual>
                  <DownloadIcon />
                </ActionList.LeadingVisual>
                Export view data
              </ActionList.Item>
            </>
          ) : null}
        </ActionList>
        {includeDisplayOptions && view.isViewStateDirty ? <ViewChangeButtons setOpen={setOpen} view={view} /> : null}
      </div>
    )
  }

  return (
    <div className={styles.viewOptionsMenuContainer}>
      <MenuAnchor
        ref={effectiveMenuAnchorRef}
        onClick={toggleViewMenu}
        viewName={view.name}
        open={displayButtonEnabled ? viewMenuOpen : open}
        showDirtyIndicator={!displayButtonEnabled}
      />
      {displayButtonEnabled ? (
        <ActionMenu anchorRef={anchorRef} open={open && !isCommandPaletteVisible} onOpenChange={handleOpenChange}>
          <ActionMenu.Overlay
            {...testIdProps('view-options-display-menu-modal')}
            width="auto"
            className={styles.viewOptionsMenuOverlay}
            variant={{regular: 'anchored', narrow: 'fullscreen'}}
          >
            {renderOverlayContent('display', displayMenuScrollRef)}
          </ActionMenu.Overlay>
        </ActionMenu>
      ) : null}
      <ActionMenu
        anchorRef={displayButtonEnabled ? viewMenuAnchorRef : anchorRef}
        open={(displayButtonEnabled ? viewMenuOpen : open) && !isCommandPaletteVisible}
        onOpenChange={handleViewMenuOpenChange}
      >
        <ActionMenu.Overlay
          {...testIdProps('view-options-menu-modal')}
          width="auto"
          className={styles.viewOptionsMenuOverlay}
          variant={{regular: 'anchored', narrow: 'fullscreen'}}
        >
          {renderOverlayContent(menuVariantForViewAnchor, viewMenuScrollRef)}
        </ActionMenu.Overlay>
      </ActionMenu>
      {currentView ? (
        <RenameViewMenu
          key={view.id}
          anchorRef={effectiveMenuAnchorRef}
          open={showSubMenu === 'rename-view'}
          setOpen={(nextOpen: boolean) => setShowSubMenu(() => (nextOpen ? 'rename-view' : null))}
          view={currentView}
          showViewOptionsMenu={showViewOptionsMenu}
        />
      ) : null}
    </div>
  )
})

const stopPropagation: React.MouseEventHandler<HTMLInputElement> = e => {
  e.stopPropagation()
}
/**
 * The control used in the roadmap view to specify the zoom level used in the view.
 */
const RenameViewMenu = memo<{
  open: boolean
  setOpen: (next: boolean) => void
  anchorRef: React.RefObject<HTMLElement | null>
  view: BaseViewState & ViewIsDirtyStates
  showViewOptionsMenu?: () => void
}>(function RenameViewMenu({open, setOpen, anchorRef, view, showViewOptionsMenu}) {
  const {renameView} = useViews()
  const [localViewName, setLocalViewName] = useSyncedState(view.name)
  const {hasWritePermissions} = ViewerPrivileges()
  const inputRef = useRef<HTMLInputElement>(null)

  const resetViewName = useCallback(() => {
    setLocalViewName(view.name)
  }, [setLocalViewName, view.name])

  const onKeyDown = useCallback(
    async (event: React.KeyboardEvent<HTMLInputElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      switch (event.key) {
        case 'Enter': {
          if (localViewName.trim().length > 0 && localViewName !== view.name) {
            await renameView(view.number, localViewName, {
              ui: ViewOptionsMenuUI,
            })
            setOpen(false)
            showViewOptionsMenu?.()
          }

          event.preventDefault()
          break
        }

        case 'Escape': {
          resetViewName()
          break
        }
      }
    },
    [localViewName, view.name, view.number, renameView, setOpen, showViewOptionsMenu, resetViewName],
  )
  const inputCompositionProps = useIgnoreKeyboardActionsWhileComposing(onKeyDown)

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalViewName(e.target.value)
    },
    [setLocalViewName],
  )

  if (!open) return null

  return (
    <Dialog
      title="Rename view"
      width="small"
      initialFocusRef={inputRef}
      returnFocusRef={anchorRef}
      onClose={() => {
        setOpen(false)
      }}
      footerButtons={[
        {
          content: Resources.cancelChanges,
          onClick: e => {
            e.preventDefault()
            resetViewName()
            setOpen(false)
          },
        },
        {
          content: Resources.saveChanges,
          buttonType: 'primary',
          onClick: async e => {
            e.preventDefault()
            if (localViewName.trim().length === 0 || localViewName === view.name) return
            await renameView(view.number, localViewName, {
              ui: ViewOptionsMenuUI,
            })
            setOpen(false)
            showViewOptionsMenu?.()
          },
        },
      ]}
    >
      <FormControl>
        <FormControl.Label>View name</FormControl.Label>
        <EmojiAutocomplete fullWidth>
          <TextInput
            ref={inputRef}
            block
            autoComplete="off"
            value={localViewName}
            onChange={onChange}
            onMouseDown={stopPropagation}
            disabled={!hasWritePermissions || view.isDeleted}
            {...inputCompositionProps}
          />
        </EmojiAutocomplete>
        {localViewName.trim().length === 0 ? (
          <FormControl.Validation variant="error">{Resources.viewNameRequired}</FormControl.Validation>
        ) : null}
      </FormControl>
    </Dialog>
  )
})

export default ViewOptionsMenu
